import type { ObjectDirective } from "vue";
import { DS_TOOLTIP_ATTRIBUTES } from "../constants/attributes";
import type { DSTooltipBindingValue, DSTooltipDirectiveOptions } from "../types/options";
import { hideTooltipForAnchor, refreshTooltipForAnchor } from "../utils/tooltipRegistry";

const PUBLIC_ATTRIBUTES = [
  DS_TOOLTIP_ATTRIBUTES.Text,
  DS_TOOLTIP_ATTRIBUTES.Title,
  DS_TOOLTIP_ATTRIBUTES.KeyboardShortcut,
  DS_TOOLTIP_ATTRIBUTES.KeyboardShortcutMac,
  DS_TOOLTIP_ATTRIBUTES.Placement,
] as const;

const optionalValue = (value: string | null | undefined): string | undefined => (value?.trim() ? value : undefined);

const removeTooltip = (anchor: HTMLElement): void => {
  hideTooltipForAnchor(anchor);
  PUBLIC_ATTRIBUTES.forEach(attribute => anchor.removeAttribute(attribute));
};

const synchronizeAttribute = (anchor: HTMLElement, attribute: string, value: string | undefined): void => {
  if (value) anchor.setAttribute(attribute, value);
  else anchor.removeAttribute(attribute);
};

const enabledOptions = (value: DSTooltipBindingValue): (DSTooltipDirectiveOptions & { text: string }) | undefined => {
  const options: DSTooltipDirectiveOptions | false | null | undefined =
    typeof value === "string" ? { text: value } : value;
  if (!options || options.disabled) return undefined;
  const text = optionalValue(options.text);
  return text ? { ...options, text } : undefined;
};

const synchronizeAttributes = (anchor: HTMLElement, value: DSTooltipBindingValue, argument?: string): void => {
  const options = enabledOptions(value);
  if (!options) {
    removeTooltip(anchor);
    return;
  }

  anchor.setAttribute(DS_TOOLTIP_ATTRIBUTES.Text, options.text);
  synchronizeAttribute(anchor, DS_TOOLTIP_ATTRIBUTES.Title, optionalValue(options.title));
  synchronizeAttribute(anchor, DS_TOOLTIP_ATTRIBUTES.KeyboardShortcut, optionalValue(options.kbd));
  synchronizeAttribute(anchor, DS_TOOLTIP_ATTRIBUTES.KeyboardShortcutMac, optionalValue(options.kbdMac));
  synchronizeAttribute(anchor, DS_TOOLTIP_ATTRIBUTES.Placement, optionalValue(argument ?? options.placement));
  refreshTooltipForAnchor(anchor);
};

export const vDsTooltip: ObjectDirective<HTMLElement, DSTooltipBindingValue> = {
  mounted(anchor, binding) {
    synchronizeAttributes(anchor, binding.value, binding.arg);
  },
  updated(anchor, binding) {
    synchronizeAttributes(anchor, binding.value, binding.arg);
  },
  unmounted(anchor) {
    removeTooltip(anchor);
  },
};
